<template>
  <b-modal
    v-model="showModal"
    id="enterPaymentModal"
    dialog-class="enter-payment-modal"
    centered
    size="md"
    :title="$t('payment_modal_title')"
    @hidden="hide"
  >
    <b-form class="enter-payment-form">
      <info-row class="info-main-row" :title="'Amount'" :sizing="3">
        <currency-input
          :disabled="!allowPartial"
          :name="$t('payment_modal_amount_label')"
          :amount.sync="amount"
          :fieldState="fieldState.amount"
        />
      </info-row>

      <info-row
        v-if="paymentMethod === systemPaymentMethods.OTHER"
        class="info-main-row"
        :title="'Platform'"
        :sizing="3"
      >
        <b-col class="input-group">
          <b-form-select
            v-model="paymentPlatform"
            class="form-control"
            :options="paymentPlatforms"
            @input="updateDescription"
          />
        </b-col>
      </info-row>

      <info-row
        v-if="paymentMethod === systemPaymentMethods.OTHER"
        class="info-main-row"
        :title="'Transaction ID'"
        :sizing="3"
      >
        <b-col class="input-group">
          <b-form-input
            v-model="transactionId"
            class="form-control"
            type="text"
            @input="updateDescription"
          />
        </b-col>
      </info-row>

      <info-row
        v-if="!paymentInfoIncomplete"
        class="info-main-row"
        :sizing="3"
        :title="'Description'"
      >
        <b-col class="input-group">
          <b-form-input
            v-model="description"
            type="text"
            required
            class="form-control"
            :title="'Description'"
            :placeholder="'Special discount for a loyal customer'"
            :readonly="paymentMethod === systemPaymentMethods.OTHER"
            :state="fieldState.description"
          />
        </b-col>
      </info-row>
    </b-form>

    <div slot="modal-footer" class="w-100">
      <span class="text-danger">{{ warningText }}</span>

      <payment-button
        v-if="paymentMethod === systemPaymentMethods.STRIPE && !!paymentType"
        :chargeDescription="description"
        :payer="payer"
        :billTotal="parseFloat(amount)"
        :chargeCallback="submitPayment"
        :disabled="loading"
        @charge="submitCharge"
      />

      <b-btn
        v-else
        class="float-right"
        variant="success"
        :disabled="paymentInfoIncomplete || loading"
        @click="triggerPayment"
      >
        {{ $t("payment_modal_button") }}
      </b-btn>
    </div>

    <spinner-loader v-if="loading" />
  </b-modal>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { PAYMENT_TYPES, PAYMENT_METHODS } from "@/config/constants";

export default {
  name: "EnterPaymentModal",

  props: {
    payer: {
      type: String,
      required: true,
      default: null,
    },
  },

  components: {
    CurrencyInput: () => import("@/components/widgets/CurrencyInput"),
    InfoRow: () => import("@/components/InfoRow"),
    PaymentButton: () => import("@/components/payments/PaymentButton"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  data() {
    return {
      showModal: null,
      amount: 0,
      allowPartial: true,
      warningText: "",
      paymentType: "",
      paymentMethod: "",
      paymentPlatform: "",
      transactionId: "",
      paymentPlatforms: [
        { text: "Paypal", value: "Paypal" },
        { text: "Bank Transfer", value: "Bank Transfer" },
        { text: "Check", value: "Check" },
        { text: "Credit Card", value: "Credit Card" },
        { text: "Debit Card", value: "Debit Card" },
        { text: "Gift Card", value: "Gift Card" },
        { text: "Other", value: "Other" },
      ],
      fieldState: {
        //true is valid, false showModals the red invalid
        amount: null,
        paymentType: null,
        paymentMethod: null,
      },
      systemPaymentMethods: PAYMENT_METHODS,
      description: "",
      loading: false,
    };
  },

  computed: {
    ...mapGetters([
      "getCurrentReader",
      "getReaderConnectionStatus",
      "getVendor",
      "paymentMethods",
    ]),

    ...mapState({
      stripeConnected: (state) => !!state.vendor.vendor.stripe_id,
    }),

    paymentInfoIncomplete() {
      return this.amount <= 0 || !this.paymentType || !this.paymentMethod;
    },

    paymentMethodLabel() {
      let paymentMethod = this.paymentMethods.find(
        (method) => method.value === this.paymentMethod
      );

      return paymentMethod ? paymentMethod.text : "Other";
    },

    paymentDescription() {
      if (this.paymentMethod !== this.systemPaymentMethods.OTHER) {
        return `${this.paymentMethodLabel} Payment for ${this.payer}`;
      }

      return this.transactionId
        ? `${this.paymentPlatform} Payment for ${this.payer} (#${this.transactionId})`
        : `${this.paymentPlatform} Payment for ${this.payer}`;
    },
  },

  methods: {
    submitCharge(paymentInfo) {
      this.loading = true;

      this.$store
        .dispatch("addStripeCharge", paymentInfo)
        .then((charge) => {
          this.submitPayment(charge);
          this.successNotification(
            `${this.getVendor.local_currency}${(charge.amount / 100).toFixed(
              2
            )} payment successful.`
          );
        })
        .catch((error) => {
          this.errorNotification(`Payment Failed: ${error}`);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    triggerPayment() {
      this.fieldState.amount = !!this.amount && this.amount > 0;
      this.fieldState.paymentType = !!this.paymentType;
      this.fieldState.paymentMethod = !!this.paymentMethod;

      if (
        this.fieldState.amount &&
        this.fieldState.paymentType &&
        this.fieldState.paymentMethod
      ) {
        this.submitPayment();
      } else {
        this.warningText = "Amount and payment type are required";
      }
    },

    submitPayment() {
      const paymentData = {
        amount: this.amount,
        paymentType: "Payment",
        paymentMethod: this.paymentMethodLabel,
        paymentTypeId: this.paymentType,
        paymentMethodId: this.paymentMethod,
        description: this.description,
      };

      this.$emit("submit", paymentData);
      this.hide();
    },

    setAmount(newAmount) {
      this.amount = newAmount;
    },

    setPaymentType(paymentType) {
      this.paymentType = paymentType;
    },

    setPaymentMethod(paymentMethod) {
      this.paymentMethod = paymentMethod;

      if (this.paymentMethod === this.systemPaymentMethods.OTHER) {
        this.paymentPlatform = this.paymentPlatforms[0].value;
      } else {
        this.paymentPlatform = "";
      }
    },

    updateDescription() {
      this.description = this.paymentDescription;
    },

    show(allowPartial = true) {
      this.allowPartial = allowPartial;
      this.showModal = true;
    },

    hide(paymentMethod) {
      this.clear();
      this.showModal = false;
    },

    clear() {
      this.amount = 0;
      this.warningText = "";
      this.paymentType = "";
      this.paymentMethod = "";
      this.paymentPlatform = "";
      this.transactionId = "";
      this.fieldState = {
        amount: null,
        paymentType: null,
      };
    },
  },

  watch: {
    paymentInfoIncomplete(oldVal, newVal) {
      if (newVal) {
        this.description = this.paymentDescription;
      }
    },
  },
};
</script>

<style lang="scss">
#enterPaymentModal {
  .modal-dialog {
    max-width: 550px !important;
  }

  .modal-header {
    height: initial;
    width: initial;
  }
  .modal-open {
    margin: auto;
  }

  .entry-label-text {
    transform: none;
  }
}

.stripe-fee-display {
  padding-left: 15px;
}

.stripe-payment-trigger {
  float: right;
}
</style>
